import React, { useEffect, useRef } from "react";
import Button from "../../components/ui/Button";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const ResearchSection = () => {
  // 1. Create a ref for the entire section
  const sectionRef = useRef(null);

  // 2. Create refs for the image and the content card
  const backgroundRef = useRef(null);
  const contentCardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 3. Set up GSAP context
    const context = gsap.context(() => {
      // Define the common ScrollTrigger settings for the section's overall entry
      const triggerSettings = {
        trigger: sectionRef.current,
        start: "top 80%", // Animation starts when section hits 80% down the viewport
        end: "center 50%", // Animation completes by the time the section is near the center
        scrub: 1.5, // Smooth scrubbing effect
        // markers: true, // Uncomment for debugging
      };

      // ----------------------------------------------------
      // A. Background Image Animation (Subtle Scale/Fade)
      // ----------------------------------------------------
      if (backgroundRef.current) {
        gsap.fromTo(
          backgroundRef.current,
          {
            opacity: 0.8,
            scale: 1.05, // Start slightly scaled up
          },
          {
            opacity: 1,
            scale: 1, // End at original scale
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          },
        );
      }

      // ----------------------------------------------------
      // B. Content Card Animation (Slide in from Right)
      // ----------------------------------------------------
      if (contentCardRef.current) {
        gsap.from(contentCardRef.current, {
          x: 100, // Start 100px off to the right
          opacity: 0,
          rotation: 1,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // C. Text Reveal Animation inside the card
      // ----------------------------------------------------
      const header = contentCardRef.current.querySelector(".research-header");
      const paragraph = contentCardRef.current.querySelector(
        ".research-paragraph",
      );
      const button = contentCardRef.current.querySelector(".research-button");

      const textElements = [header, paragraph, button];

      // We'll use a slightly different trigger start for the inner text
      const innerTriggerSettings = {
        trigger: contentCardRef.current,
        start: "top 85%", // Start when the card itself is clearly in view
        end: "center 60%",
        scrub: 1,
      };

      textElements.forEach((element, index) => {
        if (!element) return;

        // Initial State: Hidden (opacity: 0), slightly down (y: 20), clipped
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1, // Stagger effect for text/button reveal
          scrollTrigger: innerTriggerSettings,
        });
      });
    }, sectionRef);

    // 4. Cleanup
    return () => context.revert();
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
  };
  return (
    <section
      ref={sectionRef} // Attach section ref
      className="w-full mt-[65px] sm:mt-[95px] xl:mt-[130px]"
    >
      <div className="relative w-full h-[900px] sm:h-[800px] xl:h-[1080px]">
        {/* Background Images */}
        <div className="absolute inset-0 flex">
          <div
            ref={backgroundRef} // Attach background ref
            className="w-[100%] h-full bg-cover bg-center"
            style={{
              backgroundImage: "url('/assets/Research.png')",
              borderRadius: " 35px",
            }}
          ></div>
        </div>

        {/* Content Overlay */}
        <div className="absolute inset-0 flex justify-end">
          <div className="w-full max-w-[1041px] ml-auto flex items-center md:items-start ">
            <div
              ref={contentCardRef} // Attach content card ref
              className="bg-background-overlay backdrop-blur-sm rounded-2xl xl:rounded-[40px] p-6 sm:p-8 xl:p-[44px] mt-[50px] sm:mt-[75px] xl:mt-[102px] mr-0 sm:mr-8 xl:mr-[102px]"
              style={{
                boxShadow: "0px 4px 40px rgba(136, 136, 136, 1)",
              }}
            >
              <div className="space-y-4 sm:space-y-6 xl:space-y-[20px]">
                {/* Header - Added research-header class */}
                <h2 className="research-header text-[32px] sm:text-[48px] xl:text-xl font-bold leading-[40px] sm:leading-[60px] xl:leading-mega text-white font-['Parkinsans'] ">
                  Our Research
                </h2>

                {/* Paragraph - Added research-paragraph class */}
                <p className="research-paragraph text-[18px] md:text-[20px] xl:text-[24px] font-light  text-white text-justify">
                  UTPANN continuously invests in research and innovation to
                  enhance the effectiveness and sustainability of organic
                  fertilizers. The R&D team focuses on improving nutrient
                  balance, moisture stability, and microbial activity in
                  products like FOM, LFOM, and PROM, ensuring consistent
                  performance across diverse soil types and crops. Through
                  ongoing field trials and collaborations with agricultural
                  experts, UTPANN studies soil health parameters, crop
                  responses, and long-term benefits of organic inputs. This
                  commitment to scientific development enables UTPANN to deliver
                  data-driven, eco-friendly solutions that not only boost crop
                  yield but also revive soil vitality and support sustainable
                  agriculture.
                </p>

                <div className="research-button">
                  {" "}
                  {/* Added research-button class */}
                  <Button
                    onClick={() => navigate("/contact-us")}
                    text="Contact Us"
                    text_font_size="text-sm"
                    text_font_family="Parkinsans"
                    text_font_weight="font-semibold"
                    text_line_height="leading-normal"
                    text_text_align="center"
                    border_border="1px solid #ffffff"
                    border_border_radius="rounded-xl"
                    layout_width="auto"
                    padding="t=12px,r=34px,b=12px,l=34px"
                    className=" hover:bg-white hover:text-[#1C233A] text-white transition-colors duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchSection;
